import { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { memo, useCallback, useMemo } from 'react';

import { type EditorToolbarItem } from './editorActions';
import ToolbarButton from './ToolbarButton';
import { useToolbarState } from './useToolbarState';

interface EditorBubbleMenuProps {
  editor: Editor;
  items: EditorToolbarItem[];
}

/**
 * Floating formatting menu for the borderless/inline editor. Surfaces the
 * configured toolbar actions on a text selection (à la a document editor),
 * so the editor itself can render frameless and inline.
 */
const EditorBubbleMenu = memo(function EditorBubbleMenu({
  editor,
  items,
}: EditorBubbleMenuProps) {
  const toolbarState = useToolbarState(editor, items);
  // BubbleMenu dispatches a transaction whenever its `options`/`shouldShow`
  // props change identity, so keep them stable across renders.
  const options = useMemo(() => ({ placement: 'top' as const, offset: 8 }), []);
  // Only show on a non-empty selection in a focused, editable instance — a
  // caret with no selection has nothing to format, and an unfocused editor
  // (e.g. after clicking elsewhere) should not keep the menu open.
  const shouldShow = useCallback(
    ({
      editor: instance,
      state,
    }: {
      editor: Editor;
      state: { selection: { empty: boolean } };
    }) => instance.isEditable && instance.isFocused && !state.selection.empty,
    []
  );

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={shouldShow}
      options={options}
      className="flex flex-wrap items-center gap-x-0.5 rounded-full border border-gray-200 bg-white p-1 shadow-lg"
    >
      {toolbarState.map(({ item, active, disabled }) => (
        <ToolbarButton
          key={item}
          editor={editor}
          item={item}
          active={active}
          disabled={disabled}
        />
      ))}
    </BubbleMenu>
  );
});

export default EditorBubbleMenu;
